import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { MobileTabBar } from './shell/MobileTabBar';
import { AccountIconButton } from './shell/AccountIconButton';
import { MobileResourceSummary } from './shell/MobileResourceSummary';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { useAuth } from '../contexts/AuthContext';
import { COLORS } from '../constants/theme';

/**
 * SF-122 Component 0: a sibling to `Layout.tsx`, not a modification of its chrome JSX. Routes
 * that adopt the new mobile-first app-shell pattern (currently just `/province`) move under this
 * layout instead of `Layout.tsx`'s `Sidebar`+`ResourceBar` chrome.
 *
 * - Desktop: renders the same persistent `Sidebar` `Layout.tsx` renders today (reading
 *   `NAV_SECTIONS` as-is) — but not `ResourceBar`. The page's own Standing/Resources
 *   `GlanceCard`s fully replace `ResourceBar`'s content for adopted routes on desktop (see
 *   Architecture Integration's metric-by-metric mapping). `Sidebar` takes no props (SF-129
 *   removed its drawer-toggle state entirely) — its own `hidden lg:flex` CSS handles hide/show,
 *   matching `APP_SHELL_BREAKPOINT`'s 1024px pin to `Sidebar.tsx`'s own `lg:` breakpoint.
 * - Mobile: renders neither `Sidebar` nor a bespoke resource summary — see the mobile header
 *   note below. `MobileTabBar` is the only other chrome.
 *
 * Does not mount its own `ProvinceProvider` — see `ProvinceRoot` in `App.tsx`, which wraps both
 * this and `Layout.tsx` in one shared provider so navigating between the two chrome trees doesn't
 * remount province state (and re-flash the loading spinner).
 *
 * SF-138 Component 2: adds a mobile-only account header (Logout, then Profile) as a `<header>`
 * landmark sibling above `<main>` — not the first child inside it — since this is the only path
 * to Profile/Logout on any of this layout's routes at a mobile viewport (`MobileTabBar` has no
 * such entry, and `Layout.tsx`'s own floating buttons only mount under the legacy route group).
 * `position: sticky; top: 0` reuses `WarRoomOutcome.tsx`'s existing sticky-chrome pattern —
 * reachable while scrolling without overlapping each hub's own flush `<h1>` the way a `fixed`
 * overlay would.
 *
 * SF-138 follow-up: the header now also mounts `<MobileResourceSummary>` (SF-168's mobile pill +
 * alert chip + tap-to-expand sheet, previously wired only into `Layout.tsx` routes via
 * `ResourceBar`) so gold/food are glanceable without scrolling, matching what non-AppShellLayout
 * routes already have. Each of the 5 hub pages drops its own `StandingCard`/`ResourcesCard` on
 * mobile in favor of this — see `ResourceSummaryCards.tsx`.
 *
 * SF-182 Component 2: the header is one true flex row — `MobileResourceSummary` (`flex: 1 1
 * auto; min-width: 0`) and the two `AccountIconButton`s (`flex: 0 0 auto`, in-flow, no
 * `position: fixed`) — replacing the earlier `ResourceBar reserveChromeGutters` + two
 * `position: fixed` buttons composition, which double-reserved space and let the buttons paint
 * over the pill once `pillAlert` widened it. `MobileResourceSummary` is its own component
 * (not a `ResourceBar` slot prop) so `ResourceBar`/`Layout.tsx`'s existing, correctly-functioning
 * usage is untouched — see `MobileResourceSummary.tsx`.
 */
export function AppShellLayout() {
  const platform = useAppShellPlatform();
  const { logout } = useAuth();
  // SF-182: same double-tap guard as Layout.tsx's handleLogout (isLoggingOut/ariaDisabled), now
  // that Logout is an in-flow flex button rather than a fixed-position one.
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    await logout();
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.bg }}>
      <div className="flex min-h-screen mx-auto max-w-[1200px]">
        {platform === 'desktop' && <Sidebar />}
        <div className="flex-1 flex flex-col min-w-0">
          {platform === 'mobile' && (
            <header
              className="flex items-center border-b"
              style={{ position: 'sticky', top: 0, zIndex: 30, padding: '8px 16px', gap: 8, backgroundColor: COLORS.bgBar, borderColor: COLORS.borderBar }}
            >
              <MobileResourceSummary />
              <AccountIconButton
                icon="logout"
                onClick={handleLogout}
                ariaLabel="Logout"
                ariaDisabled={isLoggingOut}
                className="flex-shrink-0"
              />
              <AccountIconButton
                icon="profile"
                to="/profile"
                ariaLabel="Profile"
                className="flex-shrink-0"
              />
            </header>
          )}
          <main className="flex-1 overflow-y-auto">
            <div
              className={platform === 'desktop' ? 'max-w-[1040px] mx-auto' : 'max-w-[480px] mx-auto'}
              style={
                platform === 'desktop'
                  ? { padding: '20px 24px' }
                  : { padding: '16px', paddingBottom: '96px' }
              }
            >
              <Outlet />
            </div>
          </main>
          {platform === 'mobile' && <MobileTabBar />}
        </div>
      </div>
    </div>
  );
}
